import type {
  BusinessPlanFinancialSnapshot,
  InvestorFinancialHighlights,
} from "@/types/startup.types";

function monthIndex(monthlyCashFlow: Array<{ month: string }>, month: string) {
  return monthlyCashFlow.findIndex((row) => row.month === month);
}

function rowAtOffset(
  monthlyCashFlow: BusinessPlanFinancialSnapshot["monthlyCashFlow"],
  fromMonth: string,
  monthsAhead: number,
) {
  const startIdx = monthIndex(monthlyCashFlow, fromMonth);
  const baseIdx = startIdx >= 0 ? startIdx : 0;
  const targetIdx = Math.min(baseIdx + monthsAhead, monthlyCashFlow.length - 1);
  return monthlyCashFlow[targetIdx] ?? null;
}

function growthPercent(current: number, projected: number): number | null {
  if (current <= 0) return null;
  return Math.round(((projected - current) / current) * 1000) / 10;
}

function formatHorizonLabel(month: string) {
  const [year, mon] = month.split("-");
  return new Date(Number(year), Number(mon) - 1, 1).toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
  });
}

/** Recompute investor highlights for plans generated before this field existed */
export function resolveInvestorHighlights(
  snapshot: BusinessPlanFinancialSnapshot,
): InvestorFinancialHighlights {
  if (snapshot.investorHighlights) {
    return snapshot.investorHighlights;
  }

  const analysisMonth = snapshot.period.analysisDate.slice(0, 7);
  const currentRow =
    snapshot.monthlyCashFlow.find((row) => row.month === analysisMonth) ??
    snapshot.monthlyCashFlow[snapshot.monthlyCashFlow.length - 1];
  const atHorizon = snapshot.monthlyCashFlow[snapshot.monthlyCashFlow.length - 1];
  const at12 = rowAtOffset(snapshot.monthlyCashFlow, analysisMonth, 12);
  const netBurn = snapshot.fundingAnalysis.netBurn;

  let breakEvenMonth: string | null = null;
  const analysisIdx = monthIndex(snapshot.monthlyCashFlow, analysisMonth);
  for (let i = Math.max(analysisIdx, 0); i < snapshot.monthlyCashFlow.length; i += 1) {
    const row = snapshot.monthlyCashFlow[i];
    if (row.expenses > 0 && row.revenue >= row.expenses) {
      breakEvenMonth = row.month;
      break;
    }
  }

  const primaryScenario =
    snapshot.fundingAnalysis.scenarios.find((scenario) => scenario.name === "Growth Mode") ??
    snapshot.fundingAnalysis.scenarios[1] ??
    snapshot.fundingAnalysis.scenarios[0];

  const currentRevenue = currentRow?.revenue ?? snapshot.kpis.monthlyRevenue;
  const horizonRevenue = atHorizon?.revenue ?? currentRevenue;
  const arpu =
    snapshot.kpis.totalCustomers > 0
      ? Math.round(currentRevenue / snapshot.kpis.totalCustomers)
      : null;

  return {
    horizonLabel: formatHorizonLabel(atHorizon?.month ?? analysisMonth),
    current: {
      month: currentRow?.month ?? analysisMonth,
      revenue: currentRevenue,
      expenses: currentRow?.expenses ?? snapshot.kpis.monthlyBurn,
      customers: currentRow?.customers ?? snapshot.kpis.totalCustomers,
      cashBalance: currentRow?.cashBalance ?? snapshot.kpis.cashBalance,
      netBurn,
    },
    at12Months: at12
      ? {
          month: at12.month,
          revenue: at12.revenue,
          expenses: at12.expenses,
          customers: at12.customers,
          cashBalance: at12.cashBalance,
        }
      : null,
    atHorizon: {
      month: atHorizon?.month ?? analysisMonth,
      revenue: horizonRevenue,
      expenses: atHorizon?.expenses ?? 0,
      customers: atHorizon?.customers ?? 0,
      cashBalance: atHorizon?.cashBalance ?? 0,
    },
    revenueGrowthPercent12m: at12 ? growthPercent(currentRevenue, at12.revenue) : null,
    customerGrowthPercent12m: at12
      ? growthPercent(currentRow?.customers ?? 0, at12.customers)
      : null,
    breakEvenMonth,
    arpu,
    primaryFundingAsk: primaryScenario?.amount ?? 0,
    primaryFundingScenario: primaryScenario?.name ?? "Growth Mode",
    primaryFundingRunwayMonths: primaryScenario?.months ?? 15,
    scaleFactorRevenue:
      currentRevenue > 0
        ? Math.round((horizonRevenue / currentRevenue) * 10) / 10
        : horizonRevenue > 0
          ? horizonRevenue
          : 1,
  };
}

export type InvestorHeroKpi = {
  label: string;
  value: string;
  sub?: string;
  accent?: boolean;
};

export function buildInvestorHeroKpis(
  highlights: InvestorFinancialHighlights,
  currency: string,
): InvestorHeroKpi[] {
  const money = (value: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(value);

  const kpis: InvestorHeroKpi[] = [];
  const projectedRevenue =
    highlights.at12Months && highlights.at12Months.revenue > highlights.current.revenue
      ? { value: highlights.at12Months.revenue, label: "Projected MRR (12 mo)" }
      : highlights.atHorizon.revenue > highlights.current.revenue
        ? {
            value: highlights.atHorizon.revenue,
            label: `Projected MRR (${highlights.horizonLabel})`,
          }
        : null;

  if (projectedRevenue) {
    kpis.push({
      label: projectedRevenue.label,
      value: money(projectedRevenue.value),
      sub:
        highlights.scaleFactorRevenue > 1
          ? `${highlights.scaleFactorRevenue}x vs today`
          : "From your growth model",
      accent: true,
    });
  }

  const projectedCustomers =
    highlights.at12Months &&
    highlights.at12Months.customers > highlights.current.customers
      ? { value: highlights.at12Months.customers, label: "Projected customers (12 mo)" }
      : highlights.atHorizon.customers > highlights.current.customers
        ? {
            value: highlights.atHorizon.customers,
            label: `Projected customers (${highlights.horizonLabel})`,
          }
        : null;

  if (projectedCustomers) {
    kpis.push({
      label: projectedCustomers.label,
      value: projectedCustomers.value.toLocaleString(),
      sub: "Scaled from burn chart assumptions",
    });
  }

  if (highlights.revenueGrowthPercent12m != null && highlights.revenueGrowthPercent12m > 0) {
    kpis.push({
      label: "Revenue growth (12 mo)",
      value: `+${highlights.revenueGrowthPercent12m}%`,
      sub: "Modeled trajectory",
      accent: true,
    });
  }

  if (highlights.breakEvenMonth) {
    kpis.push({
      label: "Projected break-even",
      value: formatHorizonLabel(highlights.breakEvenMonth),
      sub: "Revenue covers operating expenses",
    });
  }

  if (highlights.primaryFundingAsk > 0) {
    kpis.push({
      label: `${highlights.primaryFundingScenario} raise`,
      value: money(highlights.primaryFundingAsk),
      sub: `${highlights.primaryFundingRunwayMonths} months runway`,
      accent: true,
    });
  }

  if (highlights.arpu != null && highlights.arpu > 0) {
    kpis.push({
      label: "ARPU",
      value: money(highlights.arpu),
      sub: "Average revenue per customer",
    });
  }

  if (kpis.length < 4 && highlights.atHorizon.revenue > 0) {
    kpis.push({
      label: `Plan horizon revenue`,
      value: money(highlights.atHorizon.revenue),
      sub: highlights.horizonLabel,
    });
  }

  return kpis.slice(0, 6);
}
